import { PlayerFace } from './PlayerFace';
import { PredictedPointsChart } from './PredictedPointsChart';
import { TeamBadge } from './TeamBadge';
import { usePlayerEnrichment } from '@hooks/transfers/usePlayerEnrichment';
import { BodyRegularBold } from '@components/atoms/typography';
import { usePlayerHistory } from '@hooks/usePlayerHistory';
import type {
  PlayerHistoryResponse,
  PlayerHistoryRow
} from '@api-service/player-history';
import type { TransferRecommendation } from '@api-service/recommendations';

const RECENT_GAMEWEEK_COUNT = 5;
// Fetch a buffer above the slot count so a player with one or two double
// gameweeks in the window still fills all five slots; without this the
// last-N slice would drop calendar gameweeks for DGW players (e.g. Haaland
// during the GW36 double would only render four slots).
const HISTORY_FETCH_LIMIT = RECENT_GAMEWEEK_COUNT + 3;
const FIXTURE_TILE_HEIGHT_CLASS = 'min-h-[52px] md:min-h-[58px]';

type ComparisonPlayer = TransferRecommendation['in'];

type RecentFixture = {
  opponentShortName: string;
  points: number;
  isHome: boolean;
};

type RecentGameweek = {
  gameweek: number;
  fixtures: RecentFixture[];
};

const toRecentGameweeks = (
  history: PlayerHistoryResponse | undefined
): RecentGameweek[] => {
  const rows = history?.data ?? [];
  const byGameweek = new Map<number, RecentFixture[]>();

  rows.forEach((entry: PlayerHistoryRow) => {
    const existing = byGameweek.get(entry.gameweek) ?? [];
    existing.push({
      opponentShortName: entry.opponent.shortName,
      points: entry.points,
      isHome: entry.isHome
    });
    byGameweek.set(entry.gameweek, existing);
  });

  return Array.from(byGameweek.entries())
    .sort(([a], [b]) => a - b)
    .map(([gameweek, fixtures]) => ({ gameweek, fixtures }))
    .slice(-RECENT_GAMEWEEK_COUNT);
};

const sumFixturePoints = (fixtures: RecentFixture[]) =>
  fixtures.reduce((sum, fixture) => sum + Math.round(fixture.points), 0);

const usePlayerRecentFixtures = (playerId: string | undefined) => {
  const { data, isLoading } = usePlayerHistory({
    id: playerId ?? '',
    limit: HISTORY_FETCH_LIMIT,
    enabled: Boolean(playerId)
  });
  const gameweeks = toRecentGameweeks(data);
  const totalPoints = gameweeks.reduce(
    (sum, week) => sum + sumFixturePoints(week.fixtures),
    0
  );
  return { gameweeks, totalPoints, isLoading };
};

type GameweekTileProps = {
  gameweek: RecentGameweek;
};

const GameweekTile = ({ gameweek }: GameweekTileProps) => {
  const fixtureCount = gameweek.fixtures.length;
  const isDouble = fixtureCount > 1;
  const totalPoints = sumFixturePoints(gameweek.fixtures);
  const isHomeForAll = gameweek.fixtures.every((fixture) => fixture.isHome);

  const titleParts = gameweek.fixtures.map(
    (fixture) =>
      `${fixture.isHome ? 'vs' : '@'} ${
        fixture.opponentShortName
      } (${Math.round(fixture.points)})`
  );

  return (
    <div
      className={`relative flex-1 ${FIXTURE_TILE_HEIGHT_CLASS} rounded-lg flex flex-col items-center justify-center gap-1 px-1 py-1.5 ${
        isHomeForAll ? 'bg-neutral-200' : 'bg-neutral-100'
      } ${isDouble ? 'ring-1 ring-primary-100' : ''}`}
      data-cy="fixture-tile"
      data-cy-gameweek={gameweek.gameweek}
      data-cy-fixture-count={fixtureCount}
      title={`Gameweek ${gameweek.gameweek} ${titleParts.join(' & ')}`}
    >
      {isDouble && (
        <span
          className="absolute top-1 right-1 rounded-sm bg-primary-600 px-1 text-[8px] font-bold uppercase tracking-wider text-white leading-none py-0.5"
          aria-label="Double gameweek"
        >
          DGW
        </span>
      )}
      {isDouble ? (
        <div className="flex items-center gap-0.5">
          {gameweek.fixtures.map((fixture, index) => (
            <TeamBadge
              key={`${gameweek.gameweek}-${fixture.opponentShortName}-${index}`}
              shortName={fixture.opponentShortName}
              size="sm"
            />
          ))}
        </div>
      ) : (
        <TeamBadge
          shortName={gameweek.fixtures[0].opponentShortName}
          size="sm"
        />
      )}
      <BodyRegularBold className="mb-0 tabular-nums leading-none">
        {totalPoints}
      </BodyRegularBold>
    </div>
  );
};

type FixtureTilesProps = {
  gameweeks: RecentGameweek[];
  isLoading: boolean;
};

const FixtureTiles = ({ gameweeks, isLoading }: FixtureTilesProps) => (
  <div className="flex flex-1 gap-1.5">
    {Array.from({ length: RECENT_GAMEWEEK_COUNT }).map((_, index) => {
      const gameweek = isLoading ? undefined : gameweeks[index];
      if (!gameweek) {
        return (
          <div
            key={`slot-${index}`}
            className={`flex-1 ${FIXTURE_TILE_HEIGHT_CLASS} rounded-lg bg-neutral-100/70`}
            data-cy="fixture-tile-empty"
          />
        );
      }
      return (
        <GameweekTile
          key={`gameweek-${gameweek.gameweek}`}
          gameweek={gameweek}
        />
      );
    })}
  </div>
);

type PlayerRowProps = {
  player: ComparisonPlayer;
  imageUrl?: string;
  kitImageUrl?: string;
  teamFullName?: string;
};

const PlayerRow = ({
  player,
  imageUrl,
  kitImageUrl,
  teamFullName
}: PlayerRowProps) => {
  const { gameweeks, totalPoints, isLoading } = usePlayerRecentFixtures(
    player.id
  );

  const playerFace = (sizeClass: string) => (
    <PlayerFace
      imageUrl={imageUrl}
      kitImageUrl={kitImageUrl}
      teamShortName={player.team.shortName}
      position={player.position}
      displayName={player.displayName}
      className={`${sizeClass} rounded-full bg-neutral-100 flex-shrink-0`}
    />
  );

  const totalPointsLabel = (
    <span className="whitespace-nowrap">
      <span className="font-sans text-base font-bold tabular-nums text-neutral-800">
        {totalPoints}
      </span>{' '}
      <span className="font-open-sans text-[10px] text-neutral-500">pts</span>
    </span>
  );

  return (
    <div data-cy="comparison-row">
      {/* Desktop: player header line, then the fixture tiles beneath. */}
      <div className="hidden md:flex md:flex-col md:gap-2">
        <div className="flex items-center gap-2.5">
          {playerFace('h-[34px] w-[34px]')}
          <div className="flex min-w-0 flex-col">
            <span className="truncate font-open-sans text-[13px] font-bold text-neutral-800 leading-tight">
              {player.displayName}
            </span>
            {teamFullName && (
              <span className="truncate font-open-sans text-[10px] text-neutral-500 leading-tight">
                {teamFullName}
              </span>
            )}
          </div>
          <div className="ml-auto text-right">{totalPointsLabel}</div>
        </div>
        <FixtureTiles gameweeks={gameweeks} isLoading={isLoading} />
      </div>

      {/* Mobile: avatar column, tiles, right-aligned total. */}
      <div className="grid grid-cols-[84px_1fr_34px] items-center gap-2.5 md:hidden">
        <div className="flex flex-col items-center gap-1">
          {playerFace('h-10 w-10')}
          <span className="w-full truncate text-center font-open-sans text-[13px] font-bold text-neutral-800 leading-tight">
            {player.displayName}
          </span>
          {teamFullName && (
            <span className="w-full truncate text-center font-open-sans text-[10px] text-neutral-500 leading-tight">
              {teamFullName}
            </span>
          )}
        </div>
        <FixtureTiles gameweeks={gameweeks} isLoading={isLoading} />
        <div className="text-right">{totalPointsLabel}</div>
      </div>
    </div>
  );
};

type PlayerComparisonCardProps = {
  transfers: TransferRecommendation[];
  selectedIndex?: number;
  className?: string;
};

export const PlayerComparisonCard = ({
  transfers,
  selectedIndex = 0,
  className
}: PlayerComparisonCardProps) => {
  const playerIds = transfers.flatMap((transfer) => [
    transfer.in.id,
    transfer.out.id
  ]);
  const { getEnrichment } = usePlayerEnrichment(playerIds);

  if (transfers.length === 0) {
    return null;
  }

  const activeTransfer = transfers[selectedIndex] ?? transfers[0];

  const comparisonHeader = (
    <header className="flex min-w-0 flex-col gap-1">
      <span className="font-open-sans text-[10px] font-bold uppercase tracking-[0.16em] text-neutral-500">
        Selected transfer
      </span>
      <h3 className="mb-0 font-sans text-[19px] font-semibold text-neutral-800 md:text-[22px]">
        {activeTransfer.in.displayName} vs. {activeTransfer.out.displayName}
      </h3>
      <p className="mb-0 font-open-sans text-xs text-neutral-600 md:text-[13px]">
        <span className="md:hidden">
          Tap a player to focus on their upcoming fixtures.
        </span>
        <span className="hidden md:inline">
          Click a player to focus on their upcoming fixtures.
        </span>
      </p>
    </header>
  );

  return (
    <div
      className={`rounded-2xl bg-white p-3.5 shadow-100 md:p-5 ${
        className ?? ''
      }`}
      data-cy="player-comparison-card"
    >
      <div className="md:grid md:grid-cols-[1fr_400px] md:items-start md:gap-4">
        <PredictedPointsChart
          header={comparisonHeader}
          inPlayer={activeTransfer.in}
          outPlayer={activeTransfer.out}
          inImageUrl={getEnrichment(activeTransfer.in.id)?.imageUrl}
          outImageUrl={getEnrichment(activeTransfer.out.id)?.imageUrl}
          inKitImageUrl={getEnrichment(activeTransfer.in.id)?.kitImageUrl}
          outKitImageUrl={getEnrichment(activeTransfer.out.id)?.kitImageUrl}
        />

        <div className="mt-4 flex flex-col gap-3.5 border-t border-neutral-200 pt-4 md:mt-0 md:border-l md:border-t-0 md:pl-5 md:pt-0">
          <header className="flex flex-col gap-1">
            <h3 className="mb-0 font-sans text-[19px] font-semibold text-neutral-800">
              Previous games
            </h3>
            <p className="mb-0 font-open-sans text-xs text-neutral-600">
              Last {RECENT_GAMEWEEK_COUNT} gameweeks for each player.
            </p>
          </header>

          <PlayerRow
            player={activeTransfer.in}
            imageUrl={getEnrichment(activeTransfer.in.id)?.imageUrl}
            kitImageUrl={getEnrichment(activeTransfer.in.id)?.kitImageUrl}
            teamFullName={getEnrichment(activeTransfer.in.id)?.team.fullName}
          />
          <div className="border-t border-neutral-200" aria-hidden />
          <PlayerRow
            player={activeTransfer.out}
            imageUrl={getEnrichment(activeTransfer.out.id)?.imageUrl}
            kitImageUrl={getEnrichment(activeTransfer.out.id)?.kitImageUrl}
            teamFullName={getEnrichment(activeTransfer.out.id)?.team.fullName}
          />
        </div>
      </div>
    </div>
  );
};
